import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Shield, Users, CheckCircle2, Award } from "lucide-react";

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "@id": "https://www.saskroofingcompany.com/about#webpage",
  url: "https://www.saskroofingcompany.com/about",
  name: "About Sask Roofing Company | Local Saskatchewan Roofers",
  isPartOf: { "@id": "https://www.saskroofingcompany.com/#website" },
  inLanguage: "en-CA",
  mainEntity: { "@id": "https://www.saskroofingcompany.com/#organization" },
};

export default function About() {
  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead
        title="About Sask Roofing Company | Local Saskatchewan Roofers"
        description="Learn about Sask Roofing Company, a Saskatchewan-owned roofing contractor with honest pricing and climate-tested roofs province-wide."
        schemaJson={[aboutSchema]}
      />

      {/* Hero Section */}
      <section className="relative w-full py-24 md:py-32 bg-black overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/fe7242f1-b408-4417-8330-aaae1161669d.png&output=webp&q=60"
            alt="Sask Roofing Company crew working on a Saskatchewan home"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent" />
        </div>

        <div className="container relative z-10 px-4 md:px-6">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight">
              Saskatchewan-Owned. <br />
              <span className="text-accent">Saskatchewan-Built.</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed">
              We're not a national chain or a fly-by-night storm chaser. We're
              your neighbors, dedicated to protecting Saskatchewan homes with
              roofs that actually withstand our climate.
            </p>
          </div>
        </div>
      </section>

      {/* Stat Callout Grid */}
      <section className="py-12 bg-background border-b">
        <div className="container px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="flex flex-col items-center text-center p-6 bg-muted/30 rounded-xl border">
              <span className="text-3xl font-bold text-primary mb-2">15+</span>
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Years in Business
              </span>
            </div>
            <div className="flex flex-col items-center text-center p-6 bg-muted/30 rounded-xl border">
              <span className="text-3xl font-bold text-primary mb-2">
                2,500+
              </span>
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Roofs Completed
              </span>
            </div>
            <div className="flex flex-col items-center text-center p-6 bg-muted/30 rounded-xl border">
              <span className="text-3xl font-bold text-primary mb-2">100%</span>
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Saskatchewan Owned
              </span>
            </div>
            <div className="flex flex-col items-center text-center p-6 bg-muted/30 rounded-xl border">
              <span className="text-3xl font-bold text-primary mb-2">
                Fully
              </span>
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Insured & WCB
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                Our Saskatchewan Story
              </h2>
              <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                <p>
                  Sask Roofing Company was founded over 15 years ago on a simple
                  premise: Saskatchewan homeowners deserve better than generic
                  roofing solutions. We started small, with just a single crew,
                  but with a big vision to raise the standard of roofing across
                  the province.
                </p>
                <p>
                  Our province sees brutal winter-to-summer temperature swings,
                  heavy snow loads, high prairie winds, and sudden hailstorms. A
                  standard roof installed to minimum code just doesn't cut it
                  here. We saw too many homeowners dealing with premature roof
                  failures, ice damming, and blow-offs simply because their
                  roofs weren't installed for our specific climate.
                </p>
                <p>
                  We set out to change that. By sourcing materials specifically
                  rated for extreme temperatures and training our local crews in
                  high-wind installation techniques, we built a reputation for
                  reliability. Today, our team has grown to multiple dedicated
                  crews serving Saskatoon, Regina, and communities
                  province-wide.
                </p>
                <p className="font-medium text-foreground border-l-4 border-accent pl-4 py-2 mt-6">
                  We provide honest, fixed pricing in writing, no surprises
                  after the contract is signed, and workmanship that we proudly
                  stand behind. Because we live here too.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="bg-muted p-6 rounded-xl border border-border/50 shadow-sm">
                  <Shield className="w-10 h-10 text-accent mb-4" />
                  <h3 className="text-xl font-bold mb-2">Fully Insured</h3>
                  <p className="text-muted-foreground text-sm">
                    Comprehensive liability and WCB coverage for your absolute
                    peace of mind.
                  </p>
                </div>
                <div className="bg-muted p-6 rounded-xl border border-border/50 shadow-sm">
                  <Users className="w-10 h-10 text-accent mb-4" />
                  <h3 className="text-xl font-bold mb-2">Local Crews</h3>
                  <p className="text-muted-foreground text-sm">
                    Experienced tradespeople who live and work in the
                    communities we serve.
                  </p>
                </div>
              </div>
              <div className="space-y-4 pt-0 sm:pt-8">
                <div className="bg-muted p-6 rounded-xl border border-border/50 shadow-sm">
                  <Award className="w-10 h-10 text-accent mb-4" />
                  <h3 className="text-xl font-bold mb-2">Premium Materials</h3>
                  <p className="text-muted-foreground text-sm">
                    Products specifically rated for high wind, hail, and extreme
                    temperatures.
                  </p>
                </div>
                <div className="bg-muted p-6 rounded-xl border border-border/50 shadow-sm">
                  <CheckCircle2 className="w-10 h-10 text-accent mb-4" />
                  <h3 className="text-xl font-bold mb-2">Real Warranties</h3>
                  <p className="text-muted-foreground text-sm">
                    Workmanship guarantees that we actually honor, backed by our
                    local presence.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quote / Icon Attribution */}
          <div className="mt-16 flex items-center justify-center gap-4 p-6 bg-muted/30 rounded-xl border mx-auto max-w-3xl text-center">
            <Award className="w-8 h-8 text-primary shrink-0" />
            <p className="text-muted-foreground font-medium">
              We install products from industry-leading manufacturers and adhere
              strictly to their installation specifications to ensure your
              warranties remain fully valid.
            </p>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 md:py-28 bg-muted/50 border-y">
        <div className="container px-4">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              How We Do Business
            </h2>
            <p className="text-lg text-muted-foreground">
              We believe in straight talk, hard work, and doing the job right
              the first time.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "Honest, Fixed Pricing",
                desc: "The price on your contract is the price you pay. We don't believe in lowballing to win the job and then hitting you with 'unforeseen' extras halfway through.",
              },
              {
                title: "Quality Over Volume",
                desc: "We don't rush jobs to move onto the next one. We take the time to ensure proper ventilation, underlayment, and flashing details are perfect.",
              },
              {
                title: "No High-Pressure Sales",
                desc: "We inspect your roof, give you the facts, provide a detailed quote, and let you decide. No gimmicks, no 'buy today' discounts.",
              },
              {
                title: "Climate Expertise",
                desc: "We don't sell generic solutions. Every roof we install is specifically engineered to handle Saskatchewan's extreme weather, from deep freezes to summer hail.",
              },
            ].map((value, i) => (
              <div
                key={i}
                className="bg-background p-6 rounded-xl border border-border shadow-sm flex flex-col"
              >
                <h3 className="text-xl font-bold mb-3 text-foreground">
                  {value.title}
                </h3>
                <p className="text-muted-foreground text-base leading-relaxed flex-grow">
                  {value.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-primary text-primary-foreground text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent"></div>
        <div className="container px-4 relative z-10 max-w-3xl mx-auto space-y-8">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
            Work With a Team You Can Trust
          </h2>
          <p className="text-xl text-primary-foreground/80">
            Get a free, no-pressure inspection and a fixed-price quote from
            Saskatchewan's trusted roofing experts.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
              asChild
            >
              <Link to="/contact">Get Your Free Roof Inspection</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent text-white border-white hover:bg-white/10 text-lg px-8 py-6 h-auto"
              asChild
            >
              <a href="tel:6397397969">Call (639) 739-7969</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
